import { Center, Link, Text, VStack } from "@chakra-ui/react";

import { safeHref, supportHrefFor } from "@/client/utils/supportHref";
import { useConfigStore } from "@/client/services/state/configStore";
import { useTranslation } from "react-i18next";

const ERROR_CODE_KEYS: Record<string, string> = {
  "0": "saml.errors.invalid-request",
  "1": "saml.errors.company-invalid",
  "2": "saml.errors.user-suspended",
  "3": "saml.errors.user-limit-reached",
  "4": "saml.errors.required-from-ip",
  "5": "saml.errors.unknown-origin",
};

export default function SamlTransitionError() {
  const { t } = useTranslation();
  const { config } = useConfigStore();

  const params = new URLSearchParams(window.location.search);
  const code = params.get("code") ?? "5";
  // Own keys only: `code` comes off the URL, and `constructor` or `__proto__` would
  // otherwise resolve to an Object.prototype member.
  const errorKey = Object.prototype.hasOwnProperty.call(ERROR_CODE_KEYS, code)
    ? ERROR_CODE_KEYS[code]
    : ERROR_CODE_KEYS["5"];

  const hasCustomSupport =
    config.ticketingBrand?.customSupport && config.ticketingBrand?.location;

  // Legacy fell back to a mailto link named with the branded support-email name
  // (saml-errors.js) so tenants configured with only a support email keep a
  // working contact path.
  const supportHref = hasCustomSupport
    ? safeHref(config.ticketingBrand.location)
    : supportHrefFor(config.supportEmailAddress);

  const supportName = hasCustomSupport ? config.ticketingBrand.link : config.supportEmailName;

  return (
    <Center
      minHeight="100vh"
      backgroundColor="blue.400"
      backgroundImage={`url(${config.publicImageUrl}404.png)`}
    >
      <VStack spacing={6} maxWidth="600px" textAlign="center" padding={8}>
        <Text as="h1" fontSize="5xl" color="white">
          {t("saml.errors.oops")}
        </Text>

        <Text color="white" fontSize="xl" whiteSpace="pre-wrap">
          {t(errorKey)}
        </Text>

        <Text color="white">
          {t("saml.errors.additional-assistance")}{" "}
          <Link
            isExternal
            href={supportHref}
            color="white"
            _hover={{ textDecoration: "underline" }}
          >
            {supportName}
          </Link>
          .
        </Text>
      </VStack>
    </Center>
  );
}
